import React from 'react';
import { useMsal } from '@azure/msal-react';
import { useDeviceVerification } from '../hooks/useDeviceVerification';

export const DeviceVerification: React.FC = () => {
  const { instance } = useMsal();
  const {
    userCode,
    loading,
    success,
    error,
    msalInitialized,
    setUserCode,
    verifyDevice,
    clearError,
  } = useDeviceVerification();

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !loading) {
      verifyDevice();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUserCode(e.target.value.toUpperCase());
    if (error) {
      clearError();
    }
  };

  if (success) {
    return (
      <div className="verification-container">
        <div className="success-content">
          <div className="success-icon">✓</div>
          <h1>Authentication Successful!</h1>
          <p>You can now close this window and return to your application.</p>
          <p className="sub-text">The iframe will automatically detect the successful authentication.</p>
          
          <button 
            onClick={() => window.close()} 
            className="close-button"
          >
            Close Window
          </button>
        </div>
        
        <style>{`
          .verification-container {
            min-height: 100vh;
            display: flex;
            align-items: center;
            justify-content: center;
            background: linear-gradient(135deg, #0078d4 0%, #106ebe 100%);
            padding: 2rem;
          }

          .success-content {
            background: white;
            padding: 3rem 2rem;
            border-radius: 16px;
            box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
            text-align: center;
            max-width: 500px;
            width: 100%;
          }

          .success-icon {
            width: 80px;
            height: 80px;
            background: #4caf50;
            color: white;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 2.5rem;
            margin: 0 auto 2rem;
            font-weight: bold;
          }

          .success-content h1 {
            color: #333;
            margin-bottom: 1rem;
            font-size: 1.8rem;
          }

          .success-content p {
            color: #666;
            margin-bottom: 1rem;
            line-height: 1.5;
          }

          .sub-text {
            font-size: 0.9rem;
            color: #888;
          }

          .close-button {
            background: #6c757d;
            color: white;
            border: none;
            padding: 12px 24px;
            border-radius: 6px;
            font-size: 14px;
            cursor: pointer;
            margin-top: 1rem;
          }

          .close-button:hover {
            background: #5a6268;
          }
        `}</style>
      </div>
    );
  }

  // Show loading screen during auto-verification
  if (loading && userCode && instance.getAllAccounts().length > 0) {
    return (
      <div className="verification-container">
        <div className="verification-content">
          <div className="header">
            <h1>Completing Authentication...</h1>
            <p>Automatically verifying device code using your existing login.</p>
          </div>
          
          <div className="auto-verify-content">
            <div className="auto-verify-spinner"></div>
            <p className="auto-verify-text">Device Code: <strong>{userCode}</strong></p>
            <p className="auto-verify-subtext">Using SSO for seamless authentication</p>
          </div>
          
          <style>{`
            .auto-verify-content {
              text-align: center;
              padding: 2rem 0;
            }
            
            .auto-verify-spinner {
              width: 40px;
              height: 40px;
              border: 4px solid #e3e3e3;
              border-top: 4px solid #0078d4;
              border-radius: 50%;
              animation: spin 1s linear infinite;
              margin: 0 auto 1rem;
            }
            
            .auto-verify-text {
              color: #333;
              font-size: 1.1rem;
              margin-bottom: 0.5rem;
            }
            
            .auto-verify-subtext {
              color: #666;
              font-size: 0.9rem;
            }
            
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      </div>
    );
  }

  return (
    <div className="verification-container">
      <div className="verification-content">
        <div className="header">
          <h1>Complete Device Authentication</h1>
          <p>Enter the code shown in your application to continue.</p>
        </div>
        
        <div className="form-section">
          <div className="code-entry">
            <label htmlFor="user-code">Device Code:</label>
            <input
              id="user-code"
              type="text"
              value={userCode}
              onChange={handleInputChange}
              onKeyPress={handleKeyPress}
              placeholder="C7SQKNPKB"
              maxLength={12}
              className="code-input"
              disabled={loading}
              autoFocus
            />
          </div>
          
          <button 
            onClick={verifyDevice}
            disabled={!userCode.trim() || loading || !msalInitialized}
            className="verify-button"
          >
            {loading ? (
              <>
                <div className="button-spinner"></div>
                Authenticating...
              </>
            ) : (
              instance.getAllAccounts().length > 0 ? 'Verify Device Code' : 'Continue to Azure AD'
            )}
          </button>
          
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}
        </div>
        
        <div className="info-section">
          <div className="info-item">
            <span className="info-icon">🔒</span>
            <span>Your authentication is secure and encrypted</span>
          </div>
          {instance.getAllAccounts().length > 0 ? (
            <div className="info-item">
              <span className="info-icon">✅</span>
              <span>Already signed in - using SSO for faster verification</span>
            </div>
          ) : (
            <div className="info-item">
              <span className="info-icon">⚡</span>
              <span>You'll be redirected to Azure AD and then back here</span>
            </div>
          )}
        </div>
      </div>
      
      <style>{`
        .verification-container {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
          padding: 2rem;
        }

        .verification-content {
          background: white;
          padding: 3rem 2rem;
          border-radius: 16px;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
          max-width: 500px;
          width: 100%;
        }

        .header {
          text-align: center;
          margin-bottom: 2rem;
        }

        .header h1 {
          color: #333;
          margin-bottom: 0.5rem;
          font-size: 1.8rem;
        }

        .header p {
          color: #666;
          font-size: 1.1rem;
        }

        .form-section {
          margin-bottom: 2rem;
        }

        .code-entry {
          margin-bottom: 1.5rem;
        }

        .code-entry label {
          display: block;
          color: #333;
          font-weight: 600;
          margin-bottom: 0.5rem;
        }

        .code-input {
          width: 100%;
          padding: 1rem;
          font-size: 1.2rem;
          font-family: 'Monaco', 'Consolas', monospace;
          font-weight: bold;
          text-align: center;
          letter-spacing: 3px;
          border: 2px solid #ddd;
          border-radius: 8px;
          background: #f8f9fa;
          transition: border-color 0.2s;
        }

        .code-input:focus {
          outline: none;
          border-color: #0078d4;
          background: white;
        }

        .code-input:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .verify-button {
          width: 100%;
          background: linear-gradient(45deg, #0078d4, #106ebe);
          color: white;
          border: none;
          padding: 1rem 2rem;
          border-radius: 8px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          min-height: 56px;
        }

        .verify-button:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(0, 120, 212, 0.3);
        }

        .verify-button:disabled {
          background: #ccc;
          cursor: not-allowed;
          transform: none;
          box-shadow: none;
        }

        .button-spinner {
          width: 16px;
          height: 16px;
          border: 2px solid transparent;
          border-top: 2px solid currentColor;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .error-message {
          margin-top: 1rem;
          padding: 1rem;
          background: #ffebee;
          border: 1px solid #f8bbd9;
          border-radius: 6px;
          color: #d32f2f;
          text-align: center;
        }

        .info-section {
          border-top: 1px solid #eee;
          padding-top: 1.5rem;
        }

        .info-item {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          margin-bottom: 0.75rem;
          color: #666;
          font-size: 0.9rem;
        }

        .info-icon {
          font-size: 1.1rem;
        }
      `}</style>
    </div>
  );
};